import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { isPremiumTier, Tier, } from 'features/premium/entities';
import { selectCurrentScreen, selectPreviousScreen } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import { scheduleNotification } from 'actions';
import useWarnOnSkip from 'login/premium/hooks/useWarnOnSkip';
import { useDisableAndroidBackButton } from 'hooks';
import Modal from 'utils/packages/Modal';
import { formatAmount, formatDateForItem } from 'utils';
import PremiumScreen from '../PremiumScreen';
import CompareButton from '../components/CompareButton';
import EmmaProModalCloseButton from '../components/EmmaProModalCloseButton';
import { useAvailableIapSubscriptions } from '../hooks';
import { selectDefaultPaywallTier, selectFloPaywallC1Inside, selectIsOnReverseTrial, selectShouldSeePremiumTrialScreen, } from '../selectors';
import { HowFreeTrialWorksView, } from '../trial/components/HowFreeTrialWorksView';
import useNavigateToPlanChangedScreen from '../hooks/useNavigatePlanChangedScreen';
import { tierThemes } from '../benefits';
const EmmaProModal = ({ navigation, route, }) => {
    const { tier } = route.params;
    const { isMicroModal } = route.params;
    const { promoProductId } = route.params;
    const { showFreeTier } = route.params;
    const { onUpgradeCallback } = route.params;
    const skipTrialScreen = route.params.skipTrialScreen || false;
    const store = useAppStore();
    const defaultPaywallTier = useAppSelector(selectDefaultPaywallTier);
    const shouldSeePremiumTrialScreen = useRef(selectShouldSeePremiumTrialScreen(store.getState(), tier || defaultPaywallTier));
    useEffect(() => {
        shouldSeePremiumTrialScreen.current = selectShouldSeePremiumTrialScreen(store.getState(), tier || defaultPaywallTier);
    }, [defaultPaywallTier, tier]);
    const howFreeTrialWorksView = useRef(null);
    const warnOnSkip = useWarnOnSkip();
    const showTrial = shouldSeePremiumTrialScreen.current && !skipTrialScreen;
    const skipTrial = useCallback(() => {
        warnOnSkip({
            shouldSeePremiumTrialScreen: showTrial,
            selectedTier: howFreeTrialWorksView.current?.selectedTier ??
                tier ??
                defaultPaywallTier,
            onConfirm: () => {
                navigation.pop();
                Amplitude.logEvent('EmmaProModal.Skip');
            },
            onDismiss: () => {
                howFreeTrialWorksView.current?.openUpgradeModal(false);
            },
        });
    }, [defaultPaywallTier, showTrial, tier, warnOnSkip]);
    const dispatch = useAppDispatch();
    const onClose = useCallback(() => {
        if (!route.params.currentAndAbovePlan &&
            !route.params.showDowngradePeriodButton) {
            // Be nice to have a place to define the notification types
            dispatch(scheduleNotification({
                delay: 10,
                type: 'SUBSCRIPTION_UPSELL',
                targetTier: showTrial ? defaultPaywallTier : tier || Tier.ultimate,
            }));
        }
        if (showTrial) {
            skipTrial();
        }
        else {
            navigation.pop();
            Amplitude.logEvent('EmmaProModal.Skip');
        }
    }, [
        defaultPaywallTier,
        route.params.currentAndAbovePlan,
        route.params.showDowngradePeriodButton,
        showTrial,
        skipTrial,
        tier,
    ]);
    useLayoutEffect(() => {
        navigation.setOptions({
            gestureEnabled: !showTrial,
            headerRight: () => (<CompareButton alwaysShow={route.params.skipTrialScreen} tier={route.params.tier}/>),
            headerLeft: () => (<EmmaProModalCloseButton onClose={onClose} showTrial={showTrial}/>),
        });
    }, [onClose, route.params.skipTrialScreen, route.params.tier, showTrial]);
    useDisableAndroidBackButton(useCallback(() => {
        if (showTrial) {
            skipTrial();
        }
        return showTrial;
    }, [showTrial, skipTrial]));
    useEffect(() => {
        const previousScreen = selectPreviousScreen(store.getState());
        const currentScreen = selectCurrentScreen(store.getState());
        Amplitude.logEvent('EmmaProModal.Open', {
            source: currentScreen === 'EmmaProModal' ? previousScreen : currentScreen,
        });
    }, []);
    const subscriptions = useAvailableIapSubscriptions();
    const isFloPaywallC1 = useAppSelector((store) => selectFloPaywallC1Inside(store, Boolean(isMicroModal)));
    const { benefitId } = route.params;
    const onViewAllPlansPressed = useCallback((selectedTier) => {
        Amplitude.logEvent('Open View Plans', { source: 'inside_app' });
        navigation.navigateDeprecated('SelectPremiumTierScreen', {
            tier: selectedTier,
            benefitId,
            onUpgradeCallback,
            isFloPaywallC1,
        });
    }, [benefitId, isFloPaywallC1, onUpgradeCallback]);
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const isOnReverseTrial = useAppSelector(selectIsOnReverseTrial);
    const showCancelFlow = showFreeTier &&
        emmaProStatus.active &&
        isPremiumTier(emmaProStatus.tier) &&
        (emmaProStatus.autoRenewing || isOnReverseTrial);
    const startCancelFunnel = useCallback(() => {
        const discount = emmaProStatus.autoRenewPrice?.discount;
        if (discount && emmaProStatus.autoRenewTier) {
            const tierName = tierThemes[emmaProStatus.autoRenewTier].name;
            const renewsOn = emmaProStatus.expiresOn
                ? ` on ${formatDateForItem(emmaProStatus.expiresOn)}`
                : '';
            const discountMessage = discount.percentOff
                ? `${discount.percentOff}%`
                : `${formatAmount(discount.amountOff, emmaProStatus.currentPrice?.currency)}`;
            const isFreeNextMonth = emmaProStatus.autoRenewPeriod === 'monthly' &&
                discount.percentOff === 100;
            Modal.showConfirmation({
                title: isFreeNextMonth
                    ? `Don’t lose your free month`
                    : `Don’t lose your ${discountMessage} discount`,
                subtitle: isFreeNextMonth
                    ? `You have a month of ${tierName} for ${formatAmount(0, emmaProStatus.currentPrice?.currency, 2)} waiting for you${renewsOn}. If you change or cancel your plan now, you’ll lose this offer.`
                    : `You have a ${discountMessage} discount waiting for your ${tierName} renewal${renewsOn}. If you change your plan, you’ll lose this offer.`,
                cancelButtonTitle: isFreeNextMonth
                    ? 'Keep my free month'
                    : 'Keep my discount',
                confirmButtonTitle: 'Still change plan',
                onConfirm: () => navigation.navigate('ManagePremiumSubscriptionCancelChurned', {
                    tier: emmaProStatus.tier,
                }),
            });
            return;
        }
        navigation.navigate('ManagePremiumSubscriptionCancelChurned', {
            tier: emmaProStatus.tier,
        });
    }, [
        emmaProStatus.autoRenewPeriod,
        emmaProStatus.autoRenewPrice?.discount,
        emmaProStatus.autoRenewTier,
        emmaProStatus.currentPrice?.currency,
        emmaProStatus.expiresOn,
        emmaProStatus.tier,
    ]);
    const hasRanUpgradeComplete = useRef(false);
    const usedFreeTrialRef = useRef(store.getState().user.user.emmaProStatus.usedFreeTrial);
    const { goToPlanChanged } = useNavigateToPlanChangedScreen(usedFreeTrialRef);
    const onUpgradeComplete = useCallback(async (_, newSku, previousSku, discount, proratedRefund) => {
        if (hasRanUpgradeComplete.current)
            return;
        hasRanUpgradeComplete.current = true;
        const previousTier = previousSku?.tier;
        if (!previousTier || !isPremiumTier(previousTier)) {
            onUpgradeCallback?.();
            navigation.navigate('PremiumSubscriptionWelcome', {
                popCount: 2,
                hasUsedFreeTrialBefore: usedFreeTrialRef.current,
            });
            return;
        }
        onClose();
        goToPlanChanged({
            newSku,
            previousSku,
            discount,
            proratedRefund,
            callback: onUpgradeCallback,
        });
    }, [goToPlanChanged, onClose, onUpgradeCallback]);
    if (showTrial) {
        return (<HowFreeTrialWorksView ref={howFreeTrialWorksView} tier={tier} benefitId={benefitId} analyticsFlow="EmmaProModal" subscriptions={subscriptions} onUpgradeComplete={onUpgradeComplete} upsellTrial={route.params.upsellTrial} onViewAllPlansPressed={onViewAllPlansPressed} isMicroModal={isMicroModal} isFloPaywallC1={isFloPaywallC1}/>);
    }
    return (<PremiumScreen navigation={navigation} subscriptions={subscriptions} onUpgradeComplete={onUpgradeComplete} tierScreen={tier} promoProductId={promoProductId} isTierRequired={route.params.isTierRequired} showDowngradePeriodButton={route.params.showDowngradePeriodButton} analyticsFlow="EmmaProModal" benefitId={benefitId} currentAndAbovePlan={route.params.currentAndAbovePlan} showFreeTier={showCancelFlow} {...(showCancelFlow ? { onContinueOnFree: startCancelFunnel } : {})}/>);
};
export default EmmaProModal;
